import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Shield, Trophy } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { EventList } from "../components/EventList";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamDetailPage() {
  const { teamId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/teams/${teamId}`).then((r) => {
      setData(r.data);
      axios.post(`${API}/track`, { type: "Team", name: r.data.team.name, link: `/teams/${teamId}` }).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [teamId]);

  if (notFound) return <div className="py-24 text-center text-slate-500">Team not found. <Link to="/teams" className="text-[#0055FF] underline">Back to Teams</Link></div>;
  if (!data) return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0055FF] border-t-transparent" /></div>;

  const { team, league, sport, events } = data;
  const facts = [
    ["Sport", team.sport], ["League", team.league], ["City", `${team.city}, ${team.country}`],
    ["Founded", team.founded], ["Home venue", team.venue], ["Colors", team.colors],
  ];

  return (
    <div>
      <PageHero topic={`${team.name} ${team.sport} stadium`} title={team.name} subtitle={`${team.sport} · ${team.city}, ${team.country} — founded ${team.founded}, playing at ${team.venue}.`} listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <Link to="/teams" data-testid="back-to-teams" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0055FF] hover:underline"><ArrowLeft className="h-4 w-4" /> <T>All Teams & Leagues</T></Link>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-1" data-testid="team-facts">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-purple-600 text-white"><Shield className="h-5 w-5" /></span>
              <h2 className="text-lg font-bold text-slate-900"><T>Club Facts</T></h2>
            </div>
            <dl className="mt-4 space-y-3">
              {facts.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2 text-sm">
                  <dt className="font-bold text-slate-500"><T>{k}</T></dt>
                  <dd className="text-right text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
            {league && (
              <Link to={`/leagues/${league.id}`} data-testid="team-league-link" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-4 py-2 text-xs font-bold text-[#0055FF] hover:bg-blue-100">
                <Trophy className="h-3.5 w-3.5" /> {league.name} →
              </Link>
            )}
            {sport && <p className="mt-4 text-xs text-slate-500"><span className="font-bold"><T>Governing body</T>:</span> <a href={sport.governing_body.url} target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline">{sport.governing_body.name}</a></p>}
          </div>
          <div className="lg:col-span-2">
            <AIContent topic={`History and identity of ${team.name}`} context={`Editorial history of ${team.name}, a ${team.sport} club from ${team.city}, ${team.country}, founded ${team.founded}, playing in the ${team.league} at ${team.venue}. Cover origins, culture, notable eras and community role. Do not invent specific match scores or statistics.`} />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-xl font-bold text-slate-900"><T>Related Events</T></h2>
          <EventList events={events} />
          <p className="mt-3 text-[11px] italic text-slate-400"><T>Curated demo data — clearly labeled, not a live feed.</T></p>
        </div>
      </div>
    </div>
  );
}
